import React, { useEffect } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Calculator, Mail, MapPin, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { LandingSeo } from '../seo/PageSeo';
import { homePath, landingPath, LANDING_PAGES } from '../seo/seoConfig';
import { RelatedLinks } from '../components/RelatedLinks';

export default function LandingPage({ pageKey = 'haegendorf' }) {
  const { t: tr } = useTranslation();
  const { lang } = useParams();
  const { pathname } = useLocation();
  const ns = LANDING_PAGES[pageKey].i18nKey;
  const t = (key, opts) => tr(key.startsWith('landing.') ? `${ns}.${key.slice(8)}` : key, opts);
  const services = t('landing.services', { returnObjects: true });
  const process = t('landing.process', { returnObjects: true });

  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  if (pathname.replace(/\/$/, '') !== landingPath(lang, pageKey)) return <Navigate to="/404" replace />;

  return (
    <main className="min-h-screen overflow-x-hidden bg-gradient-to-b from-gray-50 to-white dark:from-slate-950 dark:to-slate-900" data-testid={`landing-page-${pageKey}`}>
      <LandingSeo lang={lang} pageKey={pageKey} />

      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium mb-6">
            <MapPin className="h-4 w-4" /> {t('footer.location')}
          </div>
          <h1 className="text-[2rem] leading-tight sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6" data-testid="landing-h1">
            {t('landing.h1')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{t('landing.intro1')}</p>
          <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">{t('landing.intro2')}</p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 mb-8">
            {t('landing.servicesTitle')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <Card key={s.title} className="border-2 hover:border-blue-500/50 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{s.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">{s.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-base md:text-lg font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 mb-4">{t('landing.techTitle')}</h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t('landing.techText')}</p>
          </div>
          <div>
            <h2 className="text-base md:text-lg font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 mb-4">{t('landing.regionTitle')}</h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{t('landing.regionText')}</p>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 mb-8">{t('landing.processTitle')}</h2>
          <ol className="space-y-4">
            {process.map((step, i) => (
              <li key={step.title} className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">{i + 1}</span>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{step.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <CheckCircle2 className="h-10 w-10 text-blue-600 mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">{t('landing.ctaTitle')}</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8">{t('landing.ctaText')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
              <Link to={`${homePath(lang)}#calculator`} data-testid="landing-cta-calculator">
                <Calculator className="h-5 w-5 mr-2" /> {t('landing.ctaCalculator')}
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to={`${homePath(lang)}#contact`} data-testid="landing-cta-contact">
                <Mail className="h-5 w-5 mr-2" /> {t('landing.ctaContact')}
              </Link>
            </Button>
          </div>
          <Link to={homePath(lang)} className="inline-flex items-center gap-1 mt-8 text-sm text-gray-500 hover:text-blue-600" data-testid="landing-back-home">
            {t('landing.backHome')} <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </section>
      <RelatedLinks lang={lang} excludeLanding={pageKey} />
    </main>
  );
}
